import { SpotbuildGeneratedIdsResponse } from '@thoughtspot/blink-context';
import { getMonthDayByTimeStamp } from '@thoughtspot/date-format-util';
import { i18nKeys, translate as i18nTranslate } from '@thoughtspot/i18n';
import { UserPersona } from '@thoughtspot/session-service';
import _ from 'lodash';
import {
    ChecklistItem,
    ChecklistSubtask,
    ChecklistTaskNavigationType,
    ChecklistTaskType,
} from './checklist-modal/checklist-modal.types';

export const hasOnlyOneTrialDayLeft = (noOfDays: number) => noOfDays === 1;

export interface BannerAlertMessage {
    isVisible: boolean;
    errorMessage: string;
}
export const formatDate = (date: any) => {
    const d = new Date(date);

    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];

    const month = months[d.getMonth()];
    const day = d.getDate();
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${month} ${day}, ${year} ${hours}:${minutes}`;
};

export enum TrailInfoDataTestId {
    ContactSales = 'contact sales',
    PlanOptions = 'plan_options',
    Exclamation = 'exclamation',
    Reactivate = 'reactivate',
    InviteTeam = 'invite-team-button',
    BookExpertHelp = 'book-expert-help-button',
    BannerButtonContainer = 'free-trial-banner-buton-container',
    SparkFlow = 'trial-info-spark-flow',
}

export const BANNER_BUTTONS = {
    INVITE_TEAM: 'inviteTeam',
    CONTACT_SALES: 'contactSales',
    BOOK_EXPERT_HELP: 'bookExpertHelp',
} as const;

export type BannerButtonData = {
    label: string;
    onClick: () => void;
    dataTestId: string;
    id?: string;
};

export type TrialBannerData = {
    [BANNER_BUTTONS.INVITE_TEAM]: BannerButtonData;
    [BANNER_BUTTONS.CONTACT_SALES]: BannerButtonData;
    [BANNER_BUTTONS.BOOK_EXPERT_HELP]: BannerButtonData;
};

export const getSubscriptionCancelledDaysLeftText = (
    days: number,
    translate: (s: string, param?: object) => string,
): string => {
    switch (days) {
        case 0:
            return translate('reactivate_banner_text_zero');
        case 1:
            return translate('reactivate_banner_text_singular');
        default:
            return _.assign(
                translate('reactivate_banner_text', {
                    days,
                }),
            );
    }
};

export const trialMessageDetails = (
    currentDate: any,
    expiryDate: any,
    timezone: string,
    locale: string,
): any => {
    const remainingHours = Math.floor(
        (expiryDate - currentDate) / (1000 * 60 * 60), // in hrs
    );
    const adjustedExpiryDate = expiryDate.toLocaleString(locale, {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
        month: 'short',
        day: 'numeric',
    });
    const combinedDateAndTime = adjustedExpiryDate.split(',');

    return {
        remainingHours,
        time: combinedDateAndTime[1],
        formatedDate: combinedDateAndTime[0],
    };
};

export const getTrialDaysLeftText = (
    days: number,
    translate: (s: string, param?: object) => string,
    remainingHours: number,
    time: any,
    formatedDate: any,
    isSparkflow = false,
): string => {
    const LAST_TWO_DAYS = 2;

    if (days < 1 || remainingHours < 1) {
        return translate('trial_banner_after_expiry', {
            time,
            date: formatedDate,
        });
    }
    if (days <= LAST_TWO_DAYS && remainingHours > 0) {
        return remainingHours === 1
            ? translate('trial_banner_last_day_and_hour', {
                  time,
                  date: formatedDate,
              })
            : translate('trial_banner_last_two_days', {
                  hours: remainingHours,
                  time,
                  date: formatedDate,
              });
    }

    const trialBannerText = isSparkflow
        ? 'trial_banner_text_sparkflow'
        : 'trial_banner_text';
    return _.assign(translate(trialBannerText, { days }));
};

export const getDaysLeftText = (
    days: number,
    isSubscriptionCancelled: boolean,
    remainingHours?: number,
    time?: any,
    formatedDate?: any,
    translate: (s: string, param?: object) => string = i18nTranslate,
    isSparkflow = false,
): string =>
    isSubscriptionCancelled
        ? getSubscriptionCancelledDaysLeftText(days, translate)
        : getTrialDaysLeftText(
              days,
              translate,
              remainingHours,
              time,
              formatedDate,
              isSparkflow,
          );

export const getDiscountOfferText = (
    endDay: number,
    discount: number,
    discountOfferDuration: number,
    translate: (s: string, param: object) => string = i18nTranslate,
): string => {
    const expiryDate = getMonthDayByTimeStamp(endDay);

    return translate('trial_banner_discount_text', {
        discount,
        expiryDate,
        discountOfferDuration,
    });
};

export const discountApplicable = (
    startDay: number,
    endDay: number,
): boolean => {
    const now = Date.now();
    return startDay <= now && now <= endDay;
};

export const isOpenedFromSpotterFlag = 'isOpenedFromSpotter';

export enum ChecklistTaskKey {
    explore_insights = 'explore_insights',
    create_insights = 'create_insights',
    embed_insights = 'embed_insights',
    team_of_agents = 'team_of_agents',
}

export const getCompletedToastTitle = (
    key: ChecklistTaskKey,
    t: (key: i18nKeys, interpolate?: Record<string, any>) => string,
): string => {
    if (key === ChecklistTaskKey.team_of_agents) {
        return t('freetrial.banner.checklist.toast.agent');
    }

    const titles: Record<ChecklistTaskKey, i18nKeys> = {
        [ChecklistTaskKey.explore_insights]: 'explore_insights.title',
        [ChecklistTaskKey.create_insights]: 'create_insights.title',
        [ChecklistTaskKey.embed_insights]: 'embed_insights.title',
        [ChecklistTaskKey.team_of_agents]: 'team_of_agents.title',
    };

    const sectionHeaderText = titles[key] ?? 'explore_insights.title';

    return t('freetrial.banner.checklist.toast.completed', {
        section: t(sectionHeaderText).toLowerCase(),
    });
};

export const getTaskKeyForPersona = (persona: UserPersona) => {
    switch (persona) {
        case UserPersona.BUSINESS_PERSONA:
            return ChecklistTaskKey.explore_insights;
        case UserPersona.ANALYST_PERSONA:
            return ChecklistTaskKey.create_insights;
        case UserPersona.DEVELOPER_PERSONA:
            return ChecklistTaskKey.embed_insights;
        default:
            return ChecklistTaskKey.explore_insights;
    }
};

export const getSectionIndexFromId = (
    sparkflowInsightsData: ChecklistItem[],
    id: string,
) => {
    const index = sparkflowInsightsData.findIndex(item =>
        item.tasks.some(task => task.id === id),
    );
    return index === -1 ? 0 : index;
};

export const getSectionIndexFromSectionId = (
    sparkflowInsightsData: ChecklistItem[],
    sectionId: string,
): number => {
    const index = sparkflowInsightsData?.findIndex(
        item => item.id === sectionId,
    );
    return index === -1 ? 0 : index;
};

export const getNestedAccordionDataTestId = (id: string) =>
    `nested-checklist-accordion-${id}`;

export const getFirstIncompleteSectionIndex = (
    sparkflowInsightsData: ChecklistItem[],
    checkedItems: Set<string>,
): { index: number; sectionId: string; taskId: string } => {
    const result = { index: 0, sectionId: '', taskId: '' };

    sparkflowInsightsData?.forEach(
        (section: ChecklistItem, sectionIndex: number) => {
            if (result.taskId) return;
            section.tasks?.forEach((task: ChecklistTaskType) => {
                if (result.taskId) return;
                if (!checkedItems.has(task.id)) {
                    result.index = sectionIndex;
                    result.sectionId = section.id;
                    result.taskId = task.id;
                    return;
                }
                task.sub_tasks?.forEach((subtask: ChecklistSubtask) => {
                    if (result.taskId) return;
                    if (!checkedItems.has(subtask.id)) {
                        result.index = sectionIndex;
                        result.sectionId = section.id;
                        result.taskId = subtask.id;
                    }
                });
            });
        },
    );

    return result;
};

export const getInitialTaskExpandedState = (
    sparkflowInsightsData: ChecklistItem[],
    checkedItems: Set<string>,
): {
    taskExpandedState: Record<string, number[]>;
    completedCounts: Record<string, number>;
    firstIncompletePerSection: Record<string, string>;
} => {
    const taskExpandedState: Record<string, number[]> = {};
    const completedCounts: Record<string, number> = {};
    const firstIncompletePerSection: Record<string, string> = {};

    sparkflowInsightsData?.forEach((section: ChecklistItem) => {
        let completedCount = 0;
        let firstIncompleteTaskId = '';
        let firstUncheckedTaskIndex = -1;

        section.tasks?.forEach((task: ChecklistTaskType, index: number) => {
            if (checkedItems.has(task.id)) {
                completedCount++;
            } else {
                if (firstUncheckedTaskIndex === -1) {
                    firstUncheckedTaskIndex = index;
                }
                if (!firstIncompleteTaskId) {
                    firstIncompleteTaskId = task.id;
                }
            }
            task.sub_tasks?.forEach((subtask: ChecklistSubtask) => {
                if (checkedItems.has(subtask.id)) {
                    completedCount++;
                } else if (!firstIncompleteTaskId) {
                    firstIncompleteTaskId = subtask.id;
                }
            });
        });

        taskExpandedState[section.id] = [
            firstUncheckedTaskIndex !== -1 ? firstUncheckedTaskIndex : 0,
        ];
        completedCounts[section.id] = completedCount;
        firstIncompletePerSection[section.id] = firstIncompleteTaskId;
    });

    return { taskExpandedState, completedCounts, firstIncompletePerSection };
};

export const getTaskCompletedMetrics = (
    completedCounts: Record<string, number>,
) => ({
    taskCompletedBU: completedCounts[ChecklistTaskKey.explore_insights] || 0,
    taskCompletedAU: completedCounts[ChecklistTaskKey.create_insights] || 0,
    taskCompletedDU: completedCounts[ChecklistTaskKey.embed_insights] || 0,
});

export const findSectionByTaskId = (
    sparkflowInsightsData: ChecklistItem[],
    taskId: string,
): ChecklistItem | undefined => {
    return sparkflowInsightsData?.find((section: ChecklistItem) =>
        section.tasks?.some(
            (task: ChecklistTaskType) =>
                task.id === taskId ||
                task.sub_tasks?.some(
                    (subtask: ChecklistSubtask) => subtask.id === taskId,
                ),
        ),
    );
};

export const CHECKLIST_UPDATE_DELAY_MS = 300;

export const EXPLORE_INSIGHTS_VIEW_DASHBOARD_DELAY = 4700;

const SPARKFLOW_SELECTED_FUNCTION_KEY = 'sparkflowSelectedFunction';
const SPARKFLOW_SELECTED_INDUSTRY_KEY = 'sparkflowSelectedIndustry';

export const getSparkflowOnboardingSelections = (): {
    selectedFunction: string;
    selectedIndustry: string;
} => ({
    selectedFunction:
        localStorage.getItem(SPARKFLOW_SELECTED_FUNCTION_KEY) || '',
    selectedIndustry:
        localStorage.getItem(SPARKFLOW_SELECTED_INDUSTRY_KEY) || '',
});

const getTaskTypeUrlMap = (
    spotbuildGenResponse?: SpotbuildGeneratedIdsResponse[],
): Record<string, string> => {
    const modelId = spotbuildGenResponse?.[0]?.data_sources?.[0]?.model?.id;
    const liveboardId = spotbuildGenResponse?.[0]?.liveboard?.id;
    return {
        [ChecklistTaskNavigationType.LIVEBOARD]: `/insights/pinboard/${liveboardId}`,
        [ChecklistTaskNavigationType.PLAYGROUND]:
            '/develop?isFromSparkflow=true',
        [ChecklistTaskNavigationType.HOME]: '/insights/home',
        [ChecklistTaskNavigationType.CONNECT_OWN_DATA]:
            '/edge/connect-own-data',
        [ChecklistTaskNavigationType.MODEL]: `/data/tables/${modelId}`,
        [ChecklistTaskNavigationType.DEVELOPER_HOME]: '/develop',
        [ChecklistTaskNavigationType.SECURITY_SETTINGS]:
            '/develop/securitySettings',
        [ChecklistTaskNavigationType.SPOTTER]: `conv-assist?worksheetId=${modelId}&isFromSparkflow=true`,
    };
};

export const isSectionComplete = (
    section: ChecklistItem,
    checkedItems: Set<string>,
): boolean => {
    const allSectionTaskIds =
        section.tasks?.flatMap(task => [
            task.id,
            ...(task.sub_tasks?.map(st => st.id) ?? []),
        ]) ?? [];
    return (
        allSectionTaskIds.length > 0 &&
        allSectionTaskIds.every(id => checkedItems.has(id))
    );
};

export const getBaseUrlForTaskType = (
    taskType: string,
    spotbuildGenResponse?: SpotbuildGeneratedIdsResponse[],
): string | null => {
    return getTaskTypeUrlMap(spotbuildGenResponse)[taskType] ?? null;
};

export const removeTypename = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(removeTypename);
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce<Record<string, any>>((acc, key) => {
            if (key !== '__typename') {
                acc[key] = removeTypename(obj[key]);
            }
            return acc;
        }, {});
    }
    return obj;
};

export enum checklistDataTestId {
    CHECKLIST_BANNER_BUTTON = 'checklist-banner-button',
    CHECKLIST_SECTION_HEADER_BUTTON = 'checklist-section-header-button',
    CHECKLIST_TASK_HEADER_BUTTON = 'checklist-task-header-button',
    CHECKLIST_TASK_SEE_HOW_BUTTON = 'checklist-task-see-how-button',
    CHECKLIST_ITEM_CHECKED = 'checklist-item-checked',
}
